/**
 * Automatische Service-Erinnerungen (HU/TÜV) — Karte im Tab „Kunden".
 * Konfiguration (Aktivierung, Vorlauf, Vorlage) + Freigabe-Warteschlange.
 * Versand erfolgt NUR nach manueller Freigabe; bis zum Resend-Live-Key
 * wird der Versand nur protokolliert (Test-Modus).
 */
import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Send, RefreshCw, X, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import api from "../../lib/api";
import { toast } from "sonner";

const fmtDe = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; } };

export function ServiceRemindersCard() {
    const [open, setOpen] = useState(false);
    const [cfg, setCfg] = useState(null);
    const [pending, setPending] = useState(0);
    const [queue, setQueue] = useState([]);
    const [busy, setBusy] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [releasing, setReleasing] = useState(false);
    const [savedHint, setSavedHint] = useState(false);
    const [vstats, setVstats] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/dealer/service-reminders/config");
            setCfg(data.config);
            setPending(data.pending);
        } catch { /* ignore */ }
        try {
            const { data } = await api.get("/dealer/vouchers/stats");
            setVstats(data);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const loadQueue = async () => {
        try {
            const { data } = await api.get("/dealer/service-reminders/queue");
            setQueue(data.items || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { if (open) loadQueue(); }, [open]);

    const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const save = async () => {
        if (!cfg) return;
        setBusy(true);
        try {
            await api.put("/dealer/service-reminders/config", {
                enabled: !!cfg.enabled, lead_weeks: Number(cfg.lead_weeks) || 6,
                auto_send: !!cfg.auto_send,
                subject: cfg.subject, body: cfg.body,
                birthday_enabled: !!cfg.birthday_enabled,
                birthday_lead_days: Number(cfg.birthday_lead_days) || 7,
                birthday_subject: cfg.birthday_subject, birthday_body: cfg.birthday_body,
                birthday_voucher_enabled: !!cfg.birthday_voucher_enabled,
                birthday_voucher_pct: Number(cfg.birthday_voucher_pct) || 10,
                birthday_voucher_valid_days: Number(cfg.birthday_voucher_valid_days) || 30,
                hu_voucher_enabled: !!cfg.hu_voucher_enabled,
                hu_voucher_pct: Number(cfg.hu_voucher_pct) || 10,
                hu_voucher_valid_days: Number(cfg.hu_voucher_valid_days) || 30,
                voucher_remainder_enabled: !!cfg.voucher_remainder_enabled,
                voucher_remainder_valid_days: Number(cfg.voucher_remainder_valid_days) || 365,
                inspection_enabled: !!cfg.inspection_enabled,
                inspection_lead_days: Number(cfg.inspection_lead_days) || 14,
                inspection_subject: cfg.inspection_subject, inspection_body: cfg.inspection_body,
            });
            setSavedHint(true); setTimeout(() => setSavedHint(false), 2500);
            toast.success("Einstellungen gespeichert");
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const scan = async () => {
        setScanning(true);
        try {
            const { data } = await api.post("/dealer/service-reminders/scan");
            setPending(data.pending);
            await loadQueue();
            toast.success(`${data.added_hu ?? 0} HU + ${data.added_birthday ?? 0} Geburtstag · ${data.pending} zur Freigabe`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Prüfung fehlgeschlagen"); }
        finally { setScanning(false); }
    };

    const skip = async (id) => {
        try { await api.delete(`/dealer/service-reminders/${id}`); setQueue((q) => q.filter((x) => x.id !== id)); setPending((p) => Math.max(0, p - 1)); }
        catch { toast.error("Konnte nicht entfernt werden"); }
    };

    const release = async () => {
        if (!pending) return;
        setReleasing(true);
        try {
            const { data } = await api.post("/dealer/service-reminders/release", {});
            toast.success(`${data.sent} Erinnerung(en) ${data.mocked ? "protokolliert (Test-Modus)" : "versendet"}`);
            await load(); await loadQueue();
        } catch (e) { toast.error(e?.response?.data?.detail || "Freigabe fehlgeschlagen"); }
        finally { setReleasing(false); }
    };

    if (!cfg) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid="service-reminders-card">
            <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3" data-testid="service-reminders-toggle">
                <div className="flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-[#0EA5E9]" />
                    <span className="font-display font-bold text-base">Automatische Kunden-Erinnerungen (HU &amp; Geburtstag)</span>
                    {cfg.enabled && <span className="text-[10px] uppercase font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm">HU aktiv</span>}
                    {cfg.birthday_enabled && <span className="text-[10px] uppercase font-bold bg-pink-100 text-pink-800 px-2 py-0.5 rounded-sm">🎂 aktiv</span>}
                    {pending > 0 && <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm" data-testid="service-reminders-pending-badge">{pending} zur Freigabe</span>}
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {open && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                    <p className="text-xs text-gray-600">
                        Erinnert Kunden automatisch an die anstehende Hauptuntersuchung (geschätzt aus Erstzulassung, Kraftrad alle 24 Monate)
                        und/oder zum <strong>Geburtstag</strong> (z. B. mit Service-Einladung). <strong>Versand nach manueller Freigabe</strong> — oder mit „Automatisch versenden" komplett ohne Freigabe.
                    </p>

                    {/* Konfiguration */}
                    <div className="flex flex-wrap items-center gap-4">
                        <label className="flex items-center gap-2" data-testid="service-reminders-enabled-row">
                            <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setField("enabled", v)} data-testid="service-reminders-enabled-switch" />
                            <span className="text-sm font-medium">Tägliche Prüfung aktivieren</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Vorlauf</span>
                            <Select value={String(cfg.lead_weeks)} onValueChange={(v) => setField("lead_weeks", v)}>
                                <SelectTrigger className="h-9 w-[130px] rounded-sm" data-testid="service-reminders-weeks-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="4">4 Wochen</SelectItem>
                                    <SelectItem value="6">6 Wochen</SelectItem>
                                    <SelectItem value="8">8 Wochen</SelectItem>
                                    <SelectItem value="9">9 Wochen (~2 Monate)</SelectItem>
                                    <SelectItem value="12">12 Wochen</SelectItem>
                                </SelectContent>
                            </Select>
                        </label>
                        <label className="flex items-center gap-2" data-testid="service-reminders-autosend-row">
                            <Switch checked={!!cfg.auto_send} onCheckedChange={(v) => setField("auto_send", v)} data-testid="service-reminders-autosend-switch" />
                            <span className="text-sm font-medium">Automatisch versenden <span className="text-xs text-gray-400">(ohne manuelle Freigabe)</span></span>
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betreff</span>
                        <Input value={cfg.subject} onChange={(e) => setField("subject", e.target.value)} className="mt-1 rounded-sm" data-testid="service-reminders-subject" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht</span>
                        <Textarea value={cfg.body} onChange={(e) => setField("body", e.target.value)} rows={5} className="mt-1 rounded-sm" data-testid="service-reminders-body" />
                    </label>
                    <div className="text-[11px] text-gray-400">Platzhalter: <code>{"{anrede}"}</code> <code>{"{nachname}"}</code> <code>{"{marke}"}</code> <code>{"{modell}"}</code> <code>{"{kennzeichen}"}</code> <code>{"{hu_datum}"}</code> <code>{"{gutschein_code}"}</code> <code>{"{gutschein_prozent}"}</code> <code>{"{gutschein_gueltig_bis}"}</code></div>

                    {/* HU-Gutschein */}
                    <div className="bg-sky-50/60 border border-sky-100 rounded-sm p-3 space-y-3">
                        <label className="flex items-center gap-2" data-testid="hu-voucher-enabled-row">
                            <Switch checked={!!cfg.hu_voucher_enabled} onCheckedChange={(v) => setField("hu_voucher_enabled", v)} data-testid="hu-voucher-enabled-switch" />
                            <span className="text-sm font-medium">🎁 Gutschein an die HU-Erinnerung anhängen</span>
                        </label>
                        {cfg.hu_voucher_enabled && (
                            <div className="flex flex-wrap items-center gap-4">
                                <label className="flex items-center gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Rabatt %</span>
                                    <Input type="number" min={1} max={100} value={cfg.hu_voucher_pct ?? 10} onChange={(e) => setField("hu_voucher_pct", e.target.value)} className="h-9 w-20 rounded-sm" data-testid="hu-voucher-pct" />
                                </label>
                                <label className="flex items-center gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Gültig (Tage)</span>
                                    <Input type="number" min={1} max={365} value={cfg.hu_voucher_valid_days ?? 30} onChange={(e) => setField("hu_voucher_valid_days", e.target.value)} className="h-9 w-20 rounded-sm" data-testid="hu-voucher-days" />
                                </label>
                            </div>
                        )}
                    </div>

                    {/* Jul-2026 (User-Wunsch) — Restguthaben als neuer Gutschein */}
                    <div className="bg-emerald-50/60 border border-emerald-100 rounded-sm p-3 space-y-3">
                        <label className="flex items-center gap-2" data-testid="voucher-remainder-enabled-row">
                            <Switch checked={!!cfg.voucher_remainder_enabled} onCheckedChange={(v) => setField("voucher_remainder_enabled", v)} data-testid="voucher-remainder-enabled-switch" />
                            <span className="text-sm font-medium">♻️ Restguthaben als neuen Gutschein ausgeben</span>
                        </label>
                        <div className="text-[11px] text-gray-500">
                            Ist ein €-Gutschein höher als der Rechnungsbetrag, wird das Restguthaben automatisch als neuer Gutschein für den Kunden angelegt — statt zu verfallen.
                        </div>
                        {cfg.voucher_remainder_enabled && (
                            <label className="flex items-center gap-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Rest-Gutschein gültig (Tage)</span>
                                <Input type="number" min={1} max={1095} value={cfg.voucher_remainder_valid_days ?? 365} onChange={(e) => setField("voucher_remainder_valid_days", e.target.value)} className="h-9 w-24 rounded-sm" data-testid="voucher-remainder-days" />
                            </label>
                        )}
                    </div>

                    {/* Geburtstags-Erinnerung */}
                    <div className="border-t border-gray-100 pt-4 space-y-3">
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-2" data-testid="birthday-reminders-enabled-row">
                                <Switch checked={!!cfg.birthday_enabled} onCheckedChange={(v) => setField("birthday_enabled", v)} data-testid="birthday-reminders-enabled-switch" />
                                <span className="text-sm font-medium">🎂 Geburtstags-Erinnerungen aktivieren</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Vorlauf</span>
                                <Select value={String(cfg.birthday_lead_days)} onValueChange={(v) => setField("birthday_lead_days", v)}>
                                    <SelectTrigger className="h-9 w-[130px] rounded-sm" data-testid="birthday-reminders-days-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="0">am Geburtstag</SelectItem>
                                        <SelectItem value="3">3 Tage</SelectItem>
                                        <SelectItem value="7">7 Tage</SelectItem>
                                        <SelectItem value="14">14 Tage</SelectItem>
                                    </SelectContent>
                                </Select>
                            </label>
                        </div>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betreff (Geburtstag)</span>
                            <Input value={cfg.birthday_subject || ""} onChange={(e) => setField("birthday_subject", e.target.value)} className="mt-1 rounded-sm" data-testid="birthday-reminders-subject" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht (Geburtstag)</span>
                            <Textarea value={cfg.birthday_body || ""} onChange={(e) => setField("birthday_body", e.target.value)} rows={4} className="mt-1 rounded-sm" data-testid="birthday-reminders-body" />
                        </label>
                        <div className="text-[11px] text-gray-400">Platzhalter: <code>{"{anrede}"}</code> <code>{"{vorname}"}</code> <code>{"{nachname}"}</code> <code>{"{geburtstag}"}</code> <code>{"{alter}"}</code> <code>{"{gutschein_code}"}</code> <code>{"{gutschein_prozent}"}</code> <code>{"{gutschein_gueltig_bis}"}</code></div>

                        {/* Geburtstags-Gutschein */}
                        <div className="bg-pink-50/60 border border-pink-100 rounded-sm p-3 space-y-3">
                            <label className="flex items-center gap-2" data-testid="bday-voucher-enabled-row">
                                <Switch checked={!!cfg.birthday_voucher_enabled} onCheckedChange={(v) => setField("birthday_voucher_enabled", v)} data-testid="bday-voucher-enabled-switch" />
                                <span className="text-sm font-medium">🎁 Gutschein an die Geburtstags-E-Mail anhängen</span>
                            </label>
                            {cfg.birthday_voucher_enabled && (
                                <div className="flex flex-wrap items-center gap-4">
                                    <label className="flex items-center gap-2">
                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Rabatt %</span>
                                        <Input type="number" min={1} max={100} value={cfg.birthday_voucher_pct ?? 10} onChange={(e) => setField("birthday_voucher_pct", e.target.value)} className="h-9 w-20 rounded-sm" data-testid="bday-voucher-pct" />
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Gültig (Tage)</span>
                                        <Input type="number" min={1} max={365} value={cfg.birthday_voucher_valid_days ?? 30} onChange={(e) => setField("birthday_voucher_valid_days", e.target.value)} className="h-9 w-20 rounded-sm" data-testid="bday-voucher-days" />
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Gutschein-Statistik */}
                    {vstats && vstats.issued > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-3" data-testid="voucher-stats">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Gutschein-Statistik</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-600">
                                <span><b>{vstats.issued}</b> ausgestellt</span>
                                <span><b className="text-emerald-700">{vstats.redeemed}</b> eingelöst</span>
                                <span>Quote <b>{vstats.rate}%</b></span>
                                <span>Aktiv: {vstats.active}</span>
                                <span>Abgelaufen: {vstats.expired ?? 0}</span>
                                <span>Umsatz: <b>{Number(vstats.revenue_net || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</b></span>
                            </div>

                            {/* Aufschlüsselung nach Typ */}
                            {vstats.by_type && Object.keys(vstats.by_type).length > 0 && (
                                <div className="flex flex-wrap gap-2" data-testid="voucher-stats-by-type">
                                    {Object.entries(vstats.by_type).map(([t, v]) => {
                                        const meta = {
                                            birthday: { label: "🎂 Geburtstag", cls: "bg-pink-50 border-pink-200 text-pink-800" },
                                            hu: { label: "🔧 HU", cls: "bg-sky-50 border-sky-200 text-sky-800" },
                                            reactivation: { label: "🔁 Reaktivierung", cls: "bg-rose-50 border-rose-200 text-rose-800" },
                                        }[t] || { label: t, cls: "bg-gray-50 border-gray-200 text-gray-700" };
                                        const conv = v.issued ? Math.round((v.redeemed / v.issued) * 100) : 0;
                                        return (
                                            <span key={t} className={`text-[11px] px-2 py-0.5 rounded-sm border ${meta.cls}`} data-testid={`voucher-type-${t}`}>
                                                {meta.label}: <b>{v.redeemed}</b>/{v.issued} eingelöst (<b>{conv}%</b>) · {Number(v.revenue_net || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Monats-Zeitreihe (eingelöst) */}
                            {vstats.monthly && vstats.monthly.some((m) => m.redeemed > 0) && (
                                <div data-testid="voucher-stats-monthly">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Eingelöst pro Monat (12 Mon.)</div>
                                    <div className="flex items-end gap-1 h-20">
                                        {(() => {
                                            const max = Math.max(1, ...vstats.monthly.map((m) => m.redeemed));
                                            return vstats.monthly.map((m) => (
                                                <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5" title={`${m.month}: ${m.redeemed} eingelöst · ${Number(m.revenue_net || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`}>
                                                    <div className="w-full bg-emerald-500/80 rounded-t-sm transition-all" style={{ height: `${(m.redeemed / max) * 64}px`, minHeight: m.redeemed > 0 ? "3px" : "0px" }} />
                                                    <span className="text-[8px] text-gray-400">{m.month.slice(5)}</span>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* Top-eingelöste Service-Positionen */}
                            {vstats.top_positions && vstats.top_positions.length > 0 && (
                                <div data-testid="voucher-stats-top-positions">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Top-eingelöste Service-Positionen</div>
                                    <div className="space-y-1">
                                        {vstats.top_positions.map((p, i) => (
                                            <div key={i} className="flex items-center justify-between text-[11px] text-gray-700 border-b border-gray-100 pb-0.5 last:border-0">
                                                <span className="truncate pr-2"><span className="text-gray-400 mr-1">{i + 1}.</span>{p.description}</span>
                                                <span className="shrink-0"><b>{p.count}×</b> · {Number(p.revenue_net || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                        <Button onClick={save} disabled={busy} className="rounded-sm font-bold bg-[#111827] hover:bg-black text-white" data-testid="service-reminders-save-btn">
                            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Speichern
                        </Button>
                        {savedHint && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Gespeichert</span>}
                        <Button onClick={scan} disabled={scanning} variant="outline" className="rounded-sm font-bold border-[#0EA5E9] text-[#0EA5E9]" data-testid="service-reminders-scan-btn">
                            {scanning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Jetzt prüfen
                        </Button>
                        {cfg.last_scan && <span className="text-[11px] text-gray-400">Letzte Prüfung: {fmtDe(cfg.last_scan)}</span>}
                    </div>

                    {/* Freigabe-Warteschlange */}
                    <div className="border-t border-gray-100 pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-sm flex items-center gap-1.5">Zur Freigabe <span className="text-[#0EA5E9]" data-testid="service-reminders-queue-count">({queue.length})</span></h4>
                            {queue.length > 0 && (
                                <Button onClick={release} disabled={releasing} className="h-9 rounded-sm font-bold bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="service-reminders-release-btn">
                                    {releasing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Freigeben & senden ({queue.length})
                                </Button>
                            )}
                        </div>
                        <div className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mb-2" data-testid="service-reminders-mock-hint">
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            Test-Modus: Ohne Resend-Live-Key werden Freigaben nur protokolliert (keine echten E-Mails).
                        </div>
                        {queue.length === 0 ? (
                            <p className="text-xs text-gray-400 py-3" data-testid="service-reminders-queue-empty">Keine offenen Erinnerungen. „Jetzt prüfen" sucht fällige HU-Termine und anstehende Geburtstage.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs" data-testid="service-reminders-queue-table">
                                    <thead className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                                        <tr><th className="text-left px-3 py-2">Kunde</th><th className="text-left px-3 py-2">Typ</th><th className="text-left px-3 py-2">Anlass</th><th className="text-left px-3 py-2">Termin</th><th className="text-left px-3 py-2">E-Mail</th><th className="px-3 py-2"></th></tr>
                                    </thead>
                                    <tbody>
                                        {queue.map((r) => (
                                            <tr key={r.id} className="border-t border-gray-100" data-testid={`service-reminder-row-${r.id}`}>
                                                <td className="px-3 py-1.5 font-medium text-[#111827]">{r.customer_name}</td>
                                                <td className="px-3 py-1.5">
                                                    {r.reminder_type === "birthday"
                                                        ? <span className="text-[10px] uppercase font-bold bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded-sm">🎂 Geburtstag</span>
                                                        : r.reminder_type === "inspection"
                                                            ? <span className="text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">🔧 Inspektion</span>
                                                            : <span className="text-[10px] uppercase font-bold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-sm">HU/TÜV</span>}
                                                </td>
                                                <td className="px-3 py-1.5 text-gray-600">
                                                    {r.reminder_type === "birthday"
                                                        ? `wird ${r.age || ""} Jahre`.trim()
                                                        : `${r.brand || ""} ${r.model || ""}${r.license_plate ? ` · ${r.license_plate}` : ""}`.trim()}
                                                </td>
                                                <td className="px-3 py-1.5 font-bold text-amber-700">{fmtDe(r.due_date || r.hu_due)}</td>
                                                <td className="px-3 py-1.5 text-gray-600">{r.email}</td>
                                                <td className="px-3 py-1.5 text-right"><button onClick={() => skip(r.id)} className="text-gray-400 hover:text-red-600" title="Überspringen" data-testid={`service-reminder-skip-${r.id}`}><X className="w-4 h-4" /></button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
